import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

// Drives the "Write with AI" modal on the property form. Collects what the
// form already knows, asks the backend for clarifying questions, then
// generates a description the owner can accept, edit, or regenerate.
// The Anthropic API key never reaches the browser — these are JSON calls
// to our own server.
export default class extends Controller {
  static targets = [
    "seedStep", "loadingStep", "questionsStep", "generatingStep",
    "reviewStep", "errorStep", "highlights", "questionsContainer",
    "result", "errorMessage"
  ]

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this.reset()
  }

  reset() {
    this.seed = null
    this.questions = []
    this.#showStep("seedStep")
  }

  // Step 1 -> 2: gather seed facts from the form, fetch clarifying questions
  async fetchQuestions() {
    this.seed = this.#collectSeed()
    this.#showStep("loadingStep")
    try {
      const data = await this.#post("/properties/ai_descriptions/questions", { seed: this.seed })
      this.questions = data.questions
      this.#renderQuestions()
      this.#showStep("questionsStep")
    } catch (e) {
      this.#showError(e.message)
    }
  }

  // Step 2 -> 3: send answers, generate the description
  async generate() {
    const answers = this.#collectAnswers()
    this.#showStep("generatingStep")
    try {
      const data = await this.#post("/properties/ai_descriptions/generate", { seed: this.seed, answers })
      this.resultTarget.value = data.description
      this.#showStep("reviewStep")
    } catch (e) {
      this.#showError(e.message)
    }
  }

  regenerate() {
    this.generate()
  }

  backToSeed() {
    this.#showStep("seedStep")
  }

  // Step 3: write the description into the property form and close the modal
  useDescription() {
    const field = document.getElementById("property_description")
    if (field) {
      field.value = this.resultTarget.value
      field.dispatchEvent(new Event("input", { bubbles: true }))
    }
    this.#close()
  }

  // --- private ---

  #collectSeed() {
    return {
      title: this.#val("property_title"),
      property_type: this.#val("property_property_type"),
      city: this.#val("property_city"),
      state: this.#val("property_state"),
      acreage: this.#val("property_acreage"),
      highlights: this.hasHighlightsTarget ? this.highlightsTarget.value : "",
      activities: this.#checkedLabels("property[activity_ids][]"),
      animals: this.#checkedLabels("property[animal_type_ids][]"),
      features: this.#checkedLabels("property[property_feature_ids][]")
    }
  }

  #renderQuestions() {
    this.questionsContainerTarget.innerHTML = ""
    this.questions.forEach((question) => {
      const wrap = document.createElement("div")
      wrap.className = "mb-3"

      const label = document.createElement("label")
      label.className = "form-label"
      label.textContent = question

      const input = document.createElement("textarea")
      input.className = "form-control ai-answer"
      input.rows = 2
      input.dataset.question = question

      wrap.appendChild(label)
      wrap.appendChild(input)
      this.questionsContainerTarget.appendChild(wrap)
    })
  }

  #collectAnswers() {
    return Array.from(this.questionsContainerTarget.querySelectorAll(".ai-answer")).map((el) => ({
      question: el.dataset.question,
      answer: el.value
    }))
  }

  async #post(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        Accept: "application/json"
      },
      body: JSON.stringify(body)
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(data.error || "Something went wrong. Please try again.")
    }
    return data
  }

  #checkedLabels(name) {
    return Array.from(document.querySelectorAll(`input[name="${name}"]:checked`))
      .map((el) => el.closest(".form-check")?.querySelector(".form-check-label")?.textContent.trim())
      .filter(Boolean)
  }

  #val(id) {
    return document.getElementById(id)?.value || ""
  }

  #showStep(name) {
    const steps = {
      seedStep: this.seedStepTarget,
      loadingStep: this.loadingStepTarget,
      questionsStep: this.questionsStepTarget,
      generatingStep: this.generatingStepTarget,
      reviewStep: this.reviewStepTarget,
      errorStep: this.errorStepTarget
    }
    Object.entries(steps).forEach(([key, el]) => el.classList.toggle("d-none", key !== name))
  }

  #showError(message) {
    this.errorMessageTarget.textContent = message
    this.#showStep("errorStep")
  }

  #close() {
    const modalEl = this.element.closest(".modal")
    if (modalEl) Modal.getOrCreateInstance(modalEl).hide()
  }
}
